import { useState } from 'react';

import { FIELDS, FOOTNOTE_DISCLAIMERS_NUMBERS, CHANNELS } from '../../shared/screener-constants';
import { isModel, isStableValue } from '../../utils/sharedHelpers';
import useScreenerCommonStore from './use-screener-common-store';
import getRestrictionsText from './screener-redux-store/helpers/get-restriction-text';
import useModalVisibility from '../../hooks/use-modal-visibility';

export const INVEST_BUTTON_MODES = {
  modal: 'modal',
  link: 'link',
  hide: 'hide'
};

const getInvestButtonMode = ({ channel }, fundType, xid, isPlanInContext, restrictionText) => {
  if (isModel(fundType) || channel === CHANNELS.DYNAMICMICROSITES) {
    return INVEST_BUTTON_MODES.hide;
  }

  // Hide the invest button for plan funds with restrictions
  if (isPlanInContext) {
    if (restrictionText.length > 0) {
      return INVEST_BUTTON_MODES.hide;
    }

    if (channel === CHANNELS.MYTIAA) {
      return INVEST_BUTTON_MODES.link;
    }

    return INVEST_BUTTON_MODES.modal;
  }

  if (channel === CHANNELS.PUBLIC) {
    return INVEST_BUTTON_MODES.modal;
  }

  return INVEST_BUTTON_MODES.hide;
};

const getFundName = (fundType, xid, participantHoldings, item, planInfo) => {
  if (!isStableValue(fundType)) {
    return item[FIELDS.TCINVESTMENTNAME];
  }
  if (planInfo?.length > 1) {
    let planName;
    participantHoldings?.forEach(plan => {
      plan?.holdings?.forEach(holding => {
        if (holding?.xid === xid) {
          planName = holding?.planLegalName;
        }
      });
    });
    return planName
      ? `${item[FIELDS.TCINVESTMENTNAME]} - ${planName}`
      : item[FIELDS.TCINVESTMENTNAME];
  }
  return `${item[FIELDS.TCINVESTMENTNAME]} - ${planInfo[0]?.name}`;
};

const useRowStore = ({
  fundType,
  xid,
  item,
  isSearchedPlanInContext,
  participantHoldings,
  searchedHoldings,
  planInfo
}) => {
  const { commonState } = useScreenerCommonStore();
  const { channel, UrlDomain, UrlPrefix } = commonState;
  const { isInvestModalVisible, handleClickInvestButton, handleCloseInvestModal } =
    useModalVisibility();
  const holdingsByContext = isSearchedPlanInContext ? searchedHoldings : participantHoldings;
  let restrictionText = [];

  const isPlanInContext =
    channel === CHANNELS.MYTIAA ||
    channel === CHANNELS.DYNAMICMICROSITES ||
    isSearchedPlanInContext;

  const fundName = getFundName(fundType, xid, participantHoldings, item, planInfo);

  // RetirePlus and such Models are never restricted
  if (!isModel(fundType)) {
    restrictionText = getRestrictionsText(xid, holdingsByContext, isPlanInContext);
  }

  const investButtonMode = getInvestButtonMode(
    { channel },
    fundType,
    xid,
    isPlanInContext,
    restrictionText
  );

  const fundDetailTableData = {
    annualYield7Day: item[FIELDS.TCMNYMKTAVGANNUALYIELD7DAY],
    effectiveYield7Day: item[FIELDS.TCMMNYMKTEFFECTIVEYIELD7DAY],
    yield7DaySuperScript: item[FOOTNOTE_DISCLAIMERS_NUMBERS.CURRENTEFFECTIVEYIELDDISNUM],
    annualYield30Day: item[FIELDS.TCSECYIELD],
    annualYield30dayUnsub: item[FIELDS.TCSECYIELDUNSUB],
    annualYield30DaySuperScript: item[FOOTNOTE_DISCLAIMERS_NUMBERS.SECYEILDDISNUM],
    currentApy: item[FIELDS.TCCURRENTAPY],
    currentApySuperScript: item[FOOTNOTE_DISCLAIMERS_NUMBERS.APYDISNUM],
    currentInterestRate: item[FIELDS.TCCURRENTINTERESTRATE],
    currentInterestRateSuperScript: item[FOOTNOTE_DISCLAIMERS_NUMBERS.CURRENTINTERESTRATEDISNUM],
    currentRate: item[FIELDS.TCCURRENTRATE],
    currentRateSuperScript: item[FOOTNOTE_DISCLAIMERS_NUMBERS.CURRENTRATEDISNUM],
    dailyYtd: item[FIELDS.TCFUNDNAVYTDPCTCHANGE],
    dailyYtdAsOf: item[FIELDS.TCFUNDNAVASOFDATE],
    dayChange: item[FIELDS.TCFUNDNAV1DAYCHANGE],
    dayChangePct: item[FIELDS.TCFUNDNAV1DAYPCTCHANGE],
    minGuaranteedRate: item[FIELDS.TCGUARANTEEDMINIMUMRATE],
    nav: item[FIELDS.TCFUNDNAV],
    navSuperScript: item[FOOTNOTE_DISCLAIMERS_NUMBERS.NAVDISNUM],
    risk: item[FIELDS.RISKVSPEERS],
    shareClass: item[FIELDS.TCSHARECLASS],
    threeMonthReturn: item[FIELDS.TCAVGANNRETURNQTR3MO],
    tsvCurrentRate: item[FIELDS.TCSVCURRENTRATE],
    tsvMinGuaranteedRate: item[FIELDS.TCSVGUARANTEEDMINIMUMRATE],
    yieldAsOfDate: item[FIELDS.TCYIELDASOFDATE]
  };

  return {
    channel,
    fundDetailTableData,
    investButtonMode,
    isInvestModalVisible,
    isPlanInContext,
    handleClickInvestButton,
    handleCloseInvestModal,
    restrictionText,
    fundName,
    UrlDomain,
    UrlPrefix
  };
};

export default useRowStore;
